import React, { useMemo, useState } from 'react';

const categoryPalette = {
  A: '#1B5E3F',
  B: '#059669',
  C: '#0284C7',
  D: '#7C3AED',
  E: '#EC4899',
  F: '#F59E0B',
  G: '#EF4444',
  H: '#06B6D4',
  I: '#8B5CF6',
  J: '#6B7280',
};

function polar(cx, cy, r, angleDeg) {
  const a = (angleDeg * Math.PI) / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}

export default function ExecutionLayerRadial({
  functions,
  scores,
  selectedId,
  onSelect,
  // Back-compat with earlier prop names used in App
  activeFunctionId,
  onSelectFunction,
}) {
  const [hoveredId, setHoveredId] = useState(null);
  const currentSelectedId = selectedId || activeFunctionId;
  const handleSelect = onSelect || onSelectFunction;
  const w = 780;
  const h = 540;
  const cx = w / 2;
  const cy = h / 2;

  const byCat = useMemo(() => {
    const m = {};
    for (const fn of functions) {
      const c = fn.letter || 'J';
      if (!m[c]) m[c] = [];
      m[c].push(fn);
    }
    for (const c of Object.keys(m)) {
      m[c].sort((a, b) => a.id.localeCompare(b.id));
    }
    return m;
  }, [functions]);

  const cats = Object.keys(categoryPalette).filter((c) => byCat[c]?.length);

  // Spread categories around a ring
  const catAngles = useMemo(() => {
    const start = -110;
    const end = 110;
    const span = end - start;
    const step = cats.length > 1 ? span / (cats.length - 1) : 0;
    const map = {};
    cats.forEach((c, i) => {
      map[c] = start + step * i;
    });
    return map;
  }, [cats]);

  const nodeLayout = useMemo(() => {
    const nodes = [];
    const rCat = 170;
    const rNode = 235;

    for (const c of cats) {
      const list = byCat[c] || [];
      const base = catAngles[c] ?? 0;
      // Each category gets a little arc
      const arc = Math.min(32, 6 + list.length * 1.6);
      const start = base - arc / 2;
      const step = list.length > 1 ? arc / (list.length - 1) : 0;
      list.forEach((fn, idx) => {
        const a = start + step * idx;
        const p = polar(cx, cy, rNode, a);
        nodes.push({
          id: fn.id,
          letter: c,
          angle: a,
          x: p.x,
          y: p.y,
        });
      });
    }

    const catLabels = cats.map((c) => {
      const p = polar(cx, cy, rCat, catAngles[c] ?? 0);
      return { c, x: p.x, y: p.y };
    });

    return { nodes, catLabels };
  }, [cats, byCat, catAngles]);

  const maxScore = Math.max(1, ...functions.map((f) => scores?.get?.(f.id) || 0));

  return (
    <div style={{ width: '100%', overflowX: 'auto' }}>
      <svg width={w} height={h} style={{ display: 'block', margin: '0 auto' }}>
        <defs>
          <radialGradient id="core" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#1B5E3F" stopOpacity="0.22" />
            <stop offset="70%" stopColor="#0F3D3E" stopOpacity="0.34" />
            <stop offset="100%" stopColor="#071f20" stopOpacity="0.9" />
          </radialGradient>
          <filter id="softShadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="3" stdDeviation="4" floodColor="#000" floodOpacity="0.35" />
          </filter>
        </defs>

        {/* Core */}
        <circle cx={cx} cy={cy} r={96} fill="url(#core)" stroke="rgba(255,255,255,.14)" />
        <text x={cx} y={cy - 14} textAnchor="middle" fontSize={16} fontWeight="700" fill="rgba(255,255,255,.95)" fontFamily="Arial, sans-serif">
          People • Tools • Data
        </text>
        <text x={cx} y={cy + 14} textAnchor="middle" fontSize={12} fill="rgba(255,255,255,.68)" fontFamily="Arial, sans-serif">
          Execution Layer
        </text>

        {/* Category labels */}
        {nodeLayout.catLabels.map((l) => (
          <g key={l.c}>
            <circle cx={l.x} cy={l.y} r={18} fill={categoryPalette[l.c]} opacity={0.20} />
            <text x={l.x} y={l.y + 4} textAnchor="middle" fontSize={12} fontWeight="700" fill="rgba(255,255,255,.9)" fontFamily="Arial, sans-serif">
              {l.c}
            </text>
          </g>
        ))}

        {/* Nodes */}
        {nodeLayout.nodes.map((n) => {
          const s = scores?.get?.(n.id) || 0;
          const heat = s / maxScore;
          const r = 8 + heat * 10;
          const fill = categoryPalette[n.letter] || '#6B7280';
          const isSelected = n.id === currentSelectedId;
          const isHovered = n.id === hoveredId;
          const stroke = isSelected ? '#ffffff' : 'rgba(255,255,255,.16)';
          const strokeW = isSelected ? 2.6 : 1;
          const opacity = 0.25 + heat * 0.65;

          // Labels get noisy fast. Only show on hover/selected, or if it's hot.
          const showLabel = isSelected || isHovered || heat >= 0.65;
          return (
            <g
              key={n.id}
              onClick={() => handleSelect?.(n.id)}
              onMouseEnter={() => setHoveredId(n.id)}
              onMouseLeave={() => setHoveredId(null)}
              style={{ cursor: 'pointer' }}
            >
              <title>{n.id}</title>
              <circle cx={n.x} cy={n.y} r={r} fill={fill} opacity={opacity} stroke={stroke} strokeWidth={strokeW} filter="url(#softShadow)" />
              {showLabel ? (
                <text x={n.x} y={n.y - (r + 8)} textAnchor="middle" fontSize={11} fill="rgba(255,255,255,.78)" fontFamily="Arial, sans-serif">
                  {n.id}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
